import { wrapIndex } from '../core/math';
import type { Track } from './generate';
import type { SpeedProfile } from './speedprofile';

/** Tighter than a 55 m radius counts as a corner you must think about. Wider
 *  than a car-circuit threshold would use, because a kart track's "gentle"
 *  bends are still tight enough to cost real speed. */
const CORNER_KAPPA = 1 / 55;
/** Runs shorter than this are curvature noise, not corners. */
const MIN_CORNER_LENGTH = 8; // m
/** Corners closer together than this are one complex, not two. Short — kart
 *  circuits string corners together with barely a car-length between them,
 *  and a wider gap would merge distinct corners into one. */
const MERGE_GAP = 9; // m

/**
 * Boards count down the distance to the corner, which is the real convention —
 * a driver brakes "at the 50 board". Placing them relative to the ideal braking
 * point instead would bake the answer into the scenery and remove the decision.
 *
 * Kart braking zones are short — tens of metres, not a car's hundred-plus —
 * so a 100 m board would count down most of a straight before there is
 * anything to react to. 75/50/25 mirrors club-kart signage.
 */
const BOARD_DISTANCES = [75, 50, 25];

export interface MarkerBoard {
  index: number;
  /** Metres to corner entry. */
  distance: number;
}

export interface Corner {
  /** 1-based, in track order from the start line. */
  number: number;
  entryIndex: number;
  apexIndex: number;
  exitIndex: number;
  /** Radius at the apex, m. */
  radius: number;
  /** Grip-limited apex speed, m/s. */
  vApex: number;
  /** +1 left-hand, -1 right-hand. */
  direction: 1 | -1;
  /** Where the ideal lap starts shedding speed for this corner. */
  brakeIndex: number;
  /** Length of the braking zone, m. */
  brakeLength: number;
  /** Speed at the braking point, m/s. */
  vEntry: number;
  boards: MarkerBoard[];
}

function isCornerAt(track: Track, i: number): boolean {
  return Math.abs(track.curvature[i]) >= CORNER_KAPPA;
}

export function findCorners(track: Track, profile: SpeedProfile): Corner[] {
  const n = track.center.length;
  const ds = track.spacing;

  // Scan starting from the start line, which sits on the longest straight, so no
  // corner can straddle the seam and need special handling.
  const origin = track.startIndex;
  const flags: boolean[] = new Array(n);
  for (let s = 0; s < n; s++) flags[s] = isCornerAt(track, wrapIndex(origin + s, n));

  // Collect runs in scan-space, then merge near neighbours.
  type Run = { from: number; to: number };
  const runs: Run[] = [];
  let s = 0;
  while (s < n) {
    if (!flags[s]) {
      s++;
      continue;
    }
    let e = s;
    while (e + 1 < n && flags[e + 1]) e++;
    runs.push({ from: s, to: e });
    s = e + 1;
  }

  const mergeSteps = Math.round(MERGE_GAP / ds);
  const merged: Run[] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last && run.from - last.to <= mergeSteps) last.to = run.to;
    else merged.push({ ...run });
  }

  const minSteps = Math.round(MIN_CORNER_LENGTH / ds);
  const kept = merged.filter((r) => r.to - r.from + 1 >= minSteps);

  return kept.map((run, idx) => {
    const entryIndex = wrapIndex(origin + run.from, n);
    const exitIndex = wrapIndex(origin + run.to, n);

    // Apex is where curvature peaks.
    let apexScan = run.from;
    let peak = 0;
    for (let j = run.from; j <= run.to; j++) {
      const kappa = Math.abs(track.curvature[wrapIndex(origin + j, n)]);
      if (kappa > peak) {
        peak = kappa;
        apexScan = j;
      }
    }
    const apexIndex = wrapIndex(origin + apexScan, n);

    // The braking zone is exactly the stretch where the ideal profile is losing
    // speed into this corner — walk back from the apex while speed was higher.
    let brakeIndex = apexIndex;
    let steps = 0;
    for (let guard = 0; guard < n; guard++) {
      const prev = wrapIndex(brakeIndex - 1, n);
      if (profile.vIdeal[prev] <= profile.vIdeal[brakeIndex] + 1e-4) break;
      brakeIndex = prev;
      steps++;
    }

    const signed = track.curvature[apexIndex];
    return {
      number: idx + 1,
      entryIndex,
      apexIndex,
      exitIndex,
      radius: peak > 1e-6 ? 1 / peak : Infinity,
      vApex: profile.vIdeal[apexIndex],
      direction: signed >= 0 ? 1 : -1,
      brakeIndex,
      brakeLength: steps * ds,
      vEntry: profile.vIdeal[brakeIndex],
      boards: [],
    } satisfies Corner;
  });
}

/**
 * Place countdown boards on the approach, skipping any that would land inside
 * another corner. On a kart circuit the straights are short, so most corners get
 * the 25 and 50 boards and only the main straight earns a 100.
 */
export function placeBoards(track: Track, corners: Corner[]): void {
  const n = track.center.length;
  const ds = track.spacing;

  const occupied: boolean[] = new Array(n).fill(false);
  for (const c of corners) {
    let i = c.entryIndex;
    for (let guard = 0; guard < n; guard++) {
      occupied[i] = true;
      if (i === c.exitIndex) break;
      i = wrapIndex(i + 1, n);
    }
  }

  for (const c of corners) {
    c.boards = [];
    for (const d of BOARD_DISTANCES) {
      const steps = Math.round(d / ds);
      const idx = wrapIndex(c.entryIndex - steps, n);
      // The board has to be visible on the approach to *this* corner. If another
      // corner sits between it and the entry, the countdown is meaningless.
      let clear = true;
      for (let j = 0; j <= steps; j++) {
        if (occupied[wrapIndex(idx + j, n)] && j < steps) {
          clear = false;
          break;
        }
      }
      if (!clear) continue;
      c.boards.push({ index: idx, distance: d });
    }
  }
}

export function buildCorners(track: Track, profile: SpeedProfile): Corner[] {
  const corners = findCorners(track, profile);
  placeBoards(track, corners);
  return corners;
}

/**
 * Index range of a corner's braking zone, for the road overlay.
 *
 * Ends at the apex, not the corner entry. The apex is where the ideal profile
 * stops shedding speed, and it is the only end point guaranteed to sit ahead of
 * the braking point — on a short zone the brake point falls *after* the entry,
 * and walking to the entry would wrap the whole lap.
 */
export function brakingZoneRange(track: Track, c: Corner): number[] {
  const n = track.center.length;
  const out: number[] = [];
  let i = c.brakeIndex;
  for (let guard = 0; guard < n; guard++) {
    out.push(i);
    if (i === c.apexIndex) break;
    i = wrapIndex(i + 1, n);
  }
  return out;
}
